import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  BarChart3,
  Building2,
  Car,
  FileCheck,
  Leaf,
  MessageSquare,
  Send,
  Star,
  TrendingUp,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { StatCard } from '../../components/shared/stat-card';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/survey/responses')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('survey:view')) throw redirect({ to: '/dashboard' });
  },
  component: SurveyResponsesPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface SurveyResponseItem {
  id: string;
  permitReference: string;
  permitType: string;
  accountName: string;
  submittedAt: string | null;
  responses: Array<{ questionId: string; answer: string | number }> | null;
}

interface SurveyListResponse {
  items: SurveyResponseItem[];
  total: number;
  page: number;
  limit: number;
}

interface SurveySummary {
  totalSent: number;
  totalSubmitted: number;
  submissionRate: number;
  averageRatings: { q1: number | null; q2: number | null; q3: number | null };
  totalComments: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getAnswer(responses: SurveyResponseItem['responses'], id: string) {
  if (!responses) return null;
  return responses.find((r) => r.questionId === id)?.answer ?? null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function avgRating(summary: SurveySummary | undefined): string {
  if (!summary) return '—';
  const vals = [
    summary.averageRatings.q1,
    summary.averageRatings.q2,
    summary.averageRatings.q3,
  ].filter((v): v is number => v != null);
  if (!vals.length) return '—';
  return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
}

// ── Sub-components ────────────────────────────────────────────────────────────

const RATING_COLOR: Record<number, string> = {
  5: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  4: 'bg-green-50 border-green-200 text-green-700',
  3: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  2: 'bg-orange-50 border-orange-200 text-orange-700',
  1: 'bg-red-50 border-red-200 text-red-700',
};

function RatingPill({
  questionId,
  value,
}: { questionId: string; value: string | number | null | undefined }) {
  if (value == null) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
          {questionId.toUpperCase()}
        </span>
        <span className="text-xs text-gray-300">—</span>
      </div>
    );
  }
  const n = Math.round(Number(value));
  const cls = RATING_COLOR[n] ?? 'bg-gray-50 border-gray-200 text-gray-600';
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
        {questionId.toUpperCase()}
      </span>
      <span
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full border px-2 py-0.5 text-xs font-bold',
          cls,
        )}
      >
        {n}
      </span>
    </div>
  );
}

const PERMIT_TYPE_GRADIENT: Record<string, string> = {
  ENV: 'from-emerald-500 to-teal-600',
  WASTE: 'from-amber-500 to-orange-600',
  VEH: 'from-blue-500 to-indigo-600',
};

const PERMIT_TYPE_ICON: Record<string, React.ReactNode> = {
  ENV: <Leaf size={16} />,
  WASTE: <Building2 size={16} />,
  VEH: <Car size={16} />,
};

const PERMIT_TYPE_COLOR: Record<string, string> = {
  ENV: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  WASTE: 'border-amber-200 bg-amber-50 text-amber-700',
  VEH: 'border-blue-200 bg-blue-50 text-blue-700',
};

function PermitAvatar({ type }: { type: string }) {
  const gradient = PERMIT_TYPE_GRADIENT[type] ?? 'from-gray-400 to-gray-500';
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm',
        gradient,
      )}
    >
      {PERMIT_TYPE_ICON[type] ?? <FileCheck size={16} />}
    </div>
  );
}

// ── Columns ───────────────────────────────────────────────────────────────────

const SURVEY_COLUMNS: CardDataTableColumn[] = [
  { id: 'permit', header: 'Permit' },
  { id: 'type', header: 'Type', align: 'center' },
  { id: 'submitted', header: 'Submitted' },
  { id: 'ratings', header: 'Ratings', align: 'center' },
  { id: 'comment', header: 'Comment' },
];

const SURVEY_GRID = 'minmax(0,1.4fr) 110px 150px 140px minmax(0,1fr)';

// ── Filter types ──────────────────────────────────────────────────────────────

type SubmittedFilter = 'all' | 'submitted' | 'pending';
type TypeFilter = 'all' | 'ENV' | 'WASTE' | 'VEH';

// ── Page ──────────────────────────────────────────────────────────────────────

function SurveyResponsesPage() {
  const [submittedFilter, setSubmittedFilter] = useState<SubmittedFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  function reset() {
    setSubmittedFilter('all');
    setTypeFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  }

  const params = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
    if (submittedFilter === 'submitted') p.set('submitted', 'true');
    if (submittedFilter === 'pending') p.set('submitted', 'false');
    if (typeFilter !== 'all') p.set('permitTypeCode', typeFilter);
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    return p.toString();
  }, [submittedFilter, typeFilter, dateFrom, dateTo, page]);

  const { data, isLoading, isError } = useQuery<SurveyListResponse>({
    queryKey: ['survey-responses', params],
    queryFn: () => api.get(`survey/responses?${params}`).json<SurveyListResponse>(),
  });

  const { data: summary, isLoading: summaryLoading } = useQuery<SurveySummary>({
    queryKey: ['survey-summary'],
    queryFn: () => api.get('survey/responses/summary').json<SurveySummary>(),
    staleTime: 60_000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);
  const notSubmitted = summary ? summary.totalSent - summary.totalSubmitted : 0;

  return (
    <AppLayout>
      <PageHeader
        title="Survey Responses"
        description="Customer feedback and satisfaction ratings by permit."
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          size="sm"
          icon={<Send size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          delta="All surveys"
          value={summary?.totalSent ?? 0}
          label="Sent"
          loading={summaryLoading}
        />
        <StatCard
          size="sm"
          icon={<MessageSquare size={18} />}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-500"
          delta="Responded"
          value={summary?.totalSubmitted ?? 0}
          label="Submitted"
          loading={summaryLoading}
        />
        <StatCard
          size="sm"
          icon={<TrendingUp size={18} />}
          iconBg="bg-violet-50"
          iconColor="text-violet-500"
          delta="Response rate"
          value={summary ? `${summary.submissionRate}%` : '—'}
          label="Submission Rate"
          loading={summaryLoading}
        />
        <StatCard
          size="sm"
          icon={<Star size={18} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          delta="Q1 · Q2 · Q3 avg"
          value={avgRating(summary)}
          label="Avg Rating"
          loading={summaryLoading}
        />
        <StatCard
          size="sm"
          icon={<BarChart3 size={18} />}
          iconBg="bg-sky-50"
          iconColor="text-sky-500"
          delta="Awaiting response"
          value={notSubmitted}
          label="Pending"
          loading={summaryLoading}
        />
        <StatCard
          size="sm"
          icon={<MessageSquare size={18} />}
          iconBg="bg-orange-50"
          iconColor="text-orange-500"
          delta="Written feedback"
          value={summary?.totalComments ?? 0}
          label="Comments"
          loading={summaryLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        {/* Status filter pills */}
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {(['all', 'submitted', 'pending'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setSubmittedFilter(v);
                setPage(1);
              }}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-all',
                submittedFilter === v
                  ? 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Type filter pills */}
        <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
          {(['all', 'ENV', 'WASTE', 'VEH'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setTypeFilter(v);
                setPage(1);
              }}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-all',
                typeFilter === v
                  ? 'bg-success-500 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {v === 'all' ? 'All Types' : v}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-400 shrink-0">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium text-gray-400 shrink-0">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all"
            />
          </div>
        </div>

        {/* Reset */}
        {(submittedFilter !== 'all' || typeFilter !== 'all' || dateFrom || dateTo) && (
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-all"
          >
            <X size={12} /> Reset
          </button>
        )}

        <span className="ml-auto text-xs text-gray-400">{total} results</span>
      </div>

      {/* List */}
      <CardDataTable
        gridTemplateColumns={SURVEY_GRID}
        columns={SURVEY_COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load survey responses</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <MessageSquare size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No survey responses found</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting your filters</p>
          </div>
        }
        rows={items.map((row): CardDataTableRowDef => {
          const q1 = getAnswer(row.responses, 'q1');
          const q2 = getAnswer(row.responses, 'q2');
          const q3 = getAnswer(row.responses, 'q3');
          const comment = getAnswer(row.responses, 'q4');
          const commentStr = typeof comment === 'string' ? comment : '';

          return {
            id: row.id,
            avatar: <PermitAvatar type={row.permitType} />,
            cells: {
              permit: (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 leading-snug font-mono">
                    {row.permitReference}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-gray-400">{row.accountName}</p>
                </div>
              ),
              type: (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold',
                    PERMIT_TYPE_COLOR[row.permitType] ?? 'border-gray-200 bg-gray-50 text-gray-600',
                  )}
                >
                  {row.permitType}
                </span>
              ),
              submitted: row.submittedAt ? (
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-success-200 bg-success-50 px-2.5 py-0.5 text-xs font-semibold text-success-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
                    Submitted
                  </span>
                  <p className="mt-1 text-[10px] text-gray-400">{formatDate(row.submittedAt)}</p>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  Pending
                </span>
              ),
              ratings: (
                <div className="flex items-center justify-center gap-3">
                  <RatingPill questionId="q1" value={q1} />
                  <RatingPill questionId="q2" value={q2} />
                  <RatingPill questionId="q3" value={q3} />
                </div>
              ),
              comment: commentStr ? (
                <p className="truncate text-xs text-gray-600" title={commentStr}>
                  {commentStr}
                </p>
              ) : (
                <span className="text-xs text-gray-300">No comment</span>
              ),
            },
            expandContent: row.responses ? (
              <div className="flex flex-wrap gap-4">
                {row.responses.map((r) => (
                  <div key={r.questionId} className="flex items-center gap-2">
                    <span className="shrink-0 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-gray-500 uppercase">
                      {r.questionId}
                    </span>
                    <span className="text-xs text-gray-700">{String(r.answer)}</span>
                  </div>
                ))}
              </div>
            ) : undefined,
          };
        })}
      />

      {/* Pagination */}
      {!isLoading && totalPages > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {items.length} of {total} responses
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium transition-colors',
                    page === p
                      ? 'bg-success-500 text-white shadow-sm'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800',
                  )}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
